import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for managing video analytics (views and likes)
 * Similar to useShortsLikes but for regular videos
 */
const useVideoAnalytics = (youtubeVideoId) => {
  const [viewCount, setViewCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [playCount, setPlayCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(false);
  const viewTrackedRef = useRef(false);
  const playTrackedRef = useRef(false);
  const playTimerRef = useRef(null);

  // Local storage keys
  const LIKED_VIDEOS_KEY = 'videos_liked_list';
  const VIEWED_VIDEOS_KEY = 'videos_viewed_list';

  // Get liked videos from localStorage
  const getLikedVideos = useCallback(() => {
    try {
      const likedVideos = localStorage.getItem(LIKED_VIDEOS_KEY);
      return likedVideos ? JSON.parse(likedVideos) : [];
    } catch (err) {
      console.error('Error reading liked videos from localStorage:', err);
      return [];
    }
  }, []);

  // Save liked videos to localStorage
  const saveLikedVideos = useCallback((likedVideos) => {
    try {
      localStorage.setItem(LIKED_VIDEOS_KEY, JSON.stringify(likedVideos));
    } catch (err) {
      console.error('Error saving liked videos to localStorage:', err);
    }
  }, []);

  // Get viewed videos from localStorage
  const getViewedVideos = useCallback(() => {
    try {
      const viewedVideos = localStorage.getItem(VIEWED_VIDEOS_KEY);
      return viewedVideos ? JSON.parse(viewedVideos) : [];
    } catch (err) {
      console.error('Error reading viewed videos from localStorage:', err);
      return [];
    }
  }, []);

  // Save viewed videos to localStorage
  const saveViewedVideos = useCallback((viewedVideos) => {
    try {
      localStorage.setItem(VIEWED_VIDEOS_KEY, JSON.stringify(viewedVideos));
    } catch (err) {
      console.error('Error saving viewed videos to localStorage:', err);
    }
  }, []);

  // Fetch current analytics from Supabase
  const fetchAnalytics = useCallback(async () => {
    if (!youtubeVideoId) return;

    try {
      const { data, error } = await supabase
        .from('video_analytics')
        .select('view_count, like_count, total_play_count')
        .eq('youtube_video_id', youtubeVideoId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows found
          // Initialize with 0 counts
          setViewCount(0);
          setLikeCount(0);
          setPlayCount(0);
        } else {
          console.warn('Error fetching video analytics:', error);
        }
        return;
      }

      setViewCount(data?.view_count || 0);
      setLikeCount(data?.like_count || 0);
      setPlayCount(data?.total_play_count || 0);
    } catch (err) {
      console.warn('Error in fetchAnalytics:', err);
      setViewCount(0);
      setLikeCount(0);
      setPlayCount(0);
    }
  }, [youtubeVideoId]);

  // Check if user has liked this video
  const checkHasLiked = useCallback(() => {
    if (!youtubeVideoId) return;

    const likedVideos = getLikedVideos();
    setHasLiked(likedVideos.includes(youtubeVideoId));
  }, [youtubeVideoId, getLikedVideos]);

  // Track a view (only once per session per video)
  const trackView = useCallback(async () => {
    if (!youtubeVideoId || viewTrackedRef.current) return;

    const viewedVideos = getViewedVideos();
    const sessionKey = `${youtubeVideoId}_${new Date().toDateString()}`;
    
    // Check if already viewed today
    if (viewedVideos.includes(sessionKey)) {
      viewTrackedRef.current = true;
      return;
    }

    try {
      // Add to viewed list
      const updatedViewedVideos = [...viewedVideos, sessionKey];
      saveViewedVideos(updatedViewedVideos);
      viewTrackedRef.current = true;

      // Mark that user has watched a video (for notification prompt)
      localStorage.setItem('has_watched_video', 'true');

      // Update view count in UI immediately
      setViewCount(prev => prev + 1);

      // Update Supabase
      const { error } = await supabase
        .from('video_analytics')
        .upsert({
          youtube_video_id: youtubeVideoId,
          view_count: viewCount + 1
        }, {
          onConflict: 'youtube_video_id',
          ignoreDuplicates: false
        });

      if (error && error.code !== 'PGRST116') {
        console.warn('Error tracking view:', error);
        // Don't revert the UI update as the view was still tracked locally
      }
    } catch (err) {
      console.error('Error in trackView:', err);
    }
  }, [youtubeVideoId, viewCount, getViewedVideos, saveViewedVideos]);

  // Track a play (after 30 seconds of viewing)
  const trackPlay = useCallback(async () => {
    if (!youtubeVideoId || playTrackedRef.current) return;

    try {
      playTrackedRef.current = true;
      
      // Update play count in UI immediately
      setPlayCount(prev => prev + 1);

      // Update Supabase using RPC function
      const { error } = await supabase.rpc('increment_video_play', {
        video_id: youtubeVideoId
      });

      if (error) {
        console.warn('Error tracking play:', error);
        // Don't revert the UI update as the play was still tracked locally
      }
    } catch (err) {
      console.error('Error in trackPlay:', err);
    }
  }, [youtubeVideoId]);

  // Start play tracking timer (30 seconds)
  const startPlayTracking = useCallback(() => {
    if (!youtubeVideoId || playTimerRef.current) return;

    // Clear any existing timer
    stopPlayTracking();

    // Start new timer for 30 seconds
    playTimerRef.current = setTimeout(() => {
      trackPlay();
    }, 30000); // 30 seconds
  }, [youtubeVideoId, trackPlay]);

  // Stop play tracking timer
  const stopPlayTracking = useCallback(() => {
    if (playTimerRef.current) {
      clearTimeout(playTimerRef.current);
      playTimerRef.current = null;
    }
  }, []);

  // Toggle like status
  const toggleLike = useCallback(async () => {
    if (!youtubeVideoId || loading) return;

    setLoading(true);
    
    try {
      const likedVideos = getLikedVideos();
      const currentlyLiked = likedVideos.includes(youtubeVideoId);
      const newLikeState = !currentlyLiked;
      
      // Update local state immediately for responsive UI
      setHasLiked(newLikeState);
      
      if (newLikeState) {
        // Add like
        const newLikedVideos = [...likedVideos, youtubeVideoId];
        saveLikedVideos(newLikedVideos);
        setLikeCount(prev => prev + 1);
        
        // Update Supabase
        try {
          const { error } = await supabase
            .from('video_analytics')
            .upsert({
              youtube_video_id: youtubeVideoId,
              like_count: likeCount + 1
            }, {
              onConflict: 'youtube_video_id',
              ignoreDuplicates: false
            });
          
          if (error && error.code !== '42501' && error.code !== 'PGRST116') {
            console.warn('Error adding like to Supabase:', error);
          }
        } catch (err) {
          console.log('Supabase update skipped - using local storage only');
        }
      } else {
        // Remove like
        const newLikedVideos = likedVideos.filter(id => id !== youtubeVideoId);
        saveLikedVideos(newLikedVideos);
        setLikeCount(prev => Math.max(0, prev - 1));
        
        // Update Supabase
        try {
          const newCount = Math.max(0, likeCount - 1);
          const { error } = await supabase
            .from('video_analytics')
            .upsert({
              youtube_video_id: youtubeVideoId,
              like_count: newCount
            }, {
              onConflict: 'youtube_video_id',
              ignoreDuplicates: false
            });
          
          if (error && error.code !== '42501' && error.code !== 'PGRST116') {
            console.warn('Error removing like from Supabase:', error);
          }
        } catch (err) {
          console.log('Supabase update skipped - using local storage only');
        }
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setLoading(false);
    }
  }, [youtubeVideoId, loading, likeCount, getLikedVideos, saveLikedVideos]);

  // Initialize data when video ID changes
  useEffect(() => {
    if (youtubeVideoId) {
      viewTrackedRef.current = false;
      playTrackedRef.current = false;
      fetchAnalytics();
      checkHasLiked();
    } else {
      setViewCount(0);
      setLikeCount(0);
      setPlayCount(0);
      setHasLiked(false);
      stopPlayTracking();
    }
  }, [youtubeVideoId, fetchAnalytics, checkHasLiked, stopPlayTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPlayTracking();
    };
  }, [stopPlayTracking]);

  return {
    viewCount,
    likeCount,
    playCount,
    hasLiked,
    loading,
    toggleLike,
    trackView,
    startPlayTracking,
    stopPlayTracking
  };
};

export default useVideoAnalytics;